import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Users, Clock, Calendar, UserPlus, X, Bell, Send, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { parseDateLocal } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
// DEV-262: shared premium-data loader (contributions + rates incl. buy-up base plans)
import { fetchPlanPremiumData } from "@/hooks/usePlanPremiumData";
import { Plan, Employee, Enrollment } from "@shared/schema";
import { planTypeRank } from "@shared/schemas/planTypes";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { calculateEnrollmentPremium } from "@/lib/enrollmentCalculator";
import { isCurrentlyEffectiveEnrollment, isCurrentlyAvailablePlan } from "@/lib/enrollmentFilters";
import { upcomingPlansMessage } from "@/lib/upcomingPlans";
import { planEnrollmentCounts, formatEnrolledLabel } from "@/lib/planEnrollmentCounts";
import {
  describeEnrollmentRule,
  filterNewHires,
  filterRecentlyEligible,
  formatDaysUntilDue,
  resolveEnrollmentRule,
  toDateOnlyString,
  INITIAL_ENROLLMENT_PERIOD_DAYS,
  type ClientPlanEligibilitySetting,
} from "@/lib/enrollmentHelpers";

interface BenefitsOverviewProps {
  plansData: Plan[];
  employees: Employee[];
  clientId?: string;
  prospectId?: string;
  getEffectivePlan: (plan: Plan) => Plan;
  formatDateSafe: (date: string | Date | null | undefined) => string;
}

const BenefitsOverview = ({
  plansData,
  employees,
  clientId,
  prospectId,
  getEffectivePlan,
  formatDateSafe,
}: BenefitsOverviewProps) => {
  const { toast } = useToast();
  const entityId = clientId || prospectId;
  const clientEmployees = employees.filter((emp) => {
    if (clientId) {
      return emp.clientId === parseInt(clientId);
    }
    if (prospectId) {
      return (emp as any).prospectId === parseInt(prospectId);
    }
    return false;
  });

  // Only count currently-effective plans in the Overview summary
  const availablePlans = plansData.filter(isCurrentlyAvailablePlan);
  // Linked plans that start later: say so instead of "No plans configured".
  const upcomingMessage = upcomingPlansMessage(plansData);

  // Reminder modal state
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderEmployee, setReminderEmployee] = useState<{ id: number; name: string; enrollmentDue?: string } | null>(null);
  const [reminderMessage, setReminderMessage] = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);

  const openReminderModal = (employeeId: number, employeeName: string, enrollmentDue?: string | Date | null) => {
    // The due date is a local calendar day; toISOString() would report its
    // UTC day (the day before, west of Greenwich) and the reminder would carry
    // a different date than the table it was opened from.
    const enrollmentDueStr = enrollmentDue instanceof Date
      ? toDateOnlyString(enrollmentDue)
      : enrollmentDue;
    setReminderEmployee({ id: employeeId, name: employeeName, enrollmentDue: enrollmentDueStr || undefined });
    setReminderMessage(`Hi ${employeeName.split(' ')[0]},\n\nThis is a friendly reminder to complete your benefits enrollment. Your enrollment deadline is ${enrollmentDueStr ? formatDateSafe(enrollmentDueStr) : 'approaching soon'}.\n\nPlease log in to the employee portal to review and select your benefits options.\n\nThank you!`);
    setReminderModalOpen(true);
  };

  const handleSendReminder = async () => {
    if (!reminderEmployee) return;
    
    setSendingReminder(true);
    try {
      const res = await apiRequest("POST", "/api/reminders/send", {
        employeeId: reminderEmployee.id,
        message: reminderMessage,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to send reminder");
      }

      toast({
        title: "Reminder Sent",
        description: data?.message || `Enrollment reminder sent to ${reminderEmployee.name}`,
      });
      setReminderModalOpen(false);
      setReminderMessage("");
      setReminderEmployee(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to send reminder. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendingReminder(false);
    }
  };

  // Fetch eligibility events for this client (e.g., employment type changes)
  interface EligibilityEvent {
    id: number;
    employeeId: number;
    clientId: number;
    eventType: string;
    employmentType: string | null;
    previousEmploymentType: string | null;
    isEligible: boolean | null;
    notes: string | null;
    createdAt: string;
    employeeName?: string;
    eligibilityDate?: string | null;
    effectiveDate?: string | null;
    enrollmentDueDate?: string | null;
    hireDate?: string | null;
  }

  const { data: eligibilityEventsData, isLoading: eligibilityEventsLoading } = useQuery<{ events: EligibilityEvent[] }>({
    queryKey: [`/api/clients/${clientId}/eligibility-events?limit=10&days=30`],
    enabled: !!clientId, // Only fetch for clients, not prospects
  });

  // Dedupe by employeeId — backend only collapses identical eventTypes, so an
  // employee with both e.g. `status_change` and another isEligible=true event
  // shows up twice. Events arrive ordered by createdAt desc, so the first
  // occurrence is the most recent.
  const rawEligibilityEvents = eligibilityEventsData?.events || [];
  const eligibilityEvents = (() => {
    const seen = new Set<number>();
    return rawEligibilityEvents.filter((e) => {
      if (seen.has(e.employeeId)) return false;
      seen.add(e.employeeId);
      return true;
    });
  })();

  // Fetch enrollments for this client or prospect
  const { data: clientEnrollments = [] } = useQuery<Enrollment[]>({
    queryKey: ["/api/enrollments", entityId],
    queryFn: async () => {
      const queryParam = clientId ? `clientId=${clientId}` : `prospectId=${prospectId}`;
      const res = await fetch(`/api/enrollments?${queryParam}`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error("Failed to fetch enrollments");
      return res.json();
    },
    enabled: !!entityId,
  });

  // Compute active enrollments and aggregate by plan
  // Treat status as authoritative and align with Enrollments/AvailablePlans logic:
  // exclude waived/terminated and rows whose coverage has ended.
  const activeEnrollments = (clientEnrollments as any[]).filter(
    isCurrentlyEffectiveEnrollment
  ) as Enrollment[];

  const planAggregates = new Map<
    number,
    { employeeIds: Set<number>; totalPremium: number }
  >();
  activeEnrollments.forEach((e) => {
    // Only count base employee rows (dependentId null) to avoid double-counting
    if (e.dependentId != null) return;
    const key = e.planId;
    const agg = planAggregates.get(key) || {
      employeeIds: new Set<number>(),
      totalPremium: 0,
    };
    if (Number.isFinite(e.employeeId as any)) agg.employeeIds.add(e.employeeId);
    const premium = parseFloat((e as any).totalPremium ?? 0);
    if (Number.isFinite(premium)) agg.totalPremium += premium;
    planAggregates.set(key, agg);
  });

  // Compute premiums for enrollments that don't have totalPremium persisted
  const [computedTotalsByPlanId, setComputedTotalsByPlanId] = React.useState<
    Map<number, number>
  >(new Map());

  // Recompute only when the parts of enrollments that affect premium totals change.
  // (Avoids JSON.stringify on the full enrollment objects.)
  const premiumComputeSignature = React.useMemo(() => {
    const rows = (activeEnrollments as any[]).map((e) => ({
      id: e?.id ?? null,
      employeeId: e?.employeeId ?? null,
      dependentId: e?.dependentId ?? null,
      planId: e?.planId ?? null,
      totalPremium: e?.totalPremium ?? null,
      coverageTier: e?.coverageTier ?? null,
      status: e?.status ?? null,
      waivedCoverage: e?.waivedCoverage ?? null,
      endDate: e?.endDate ?? null,
    }));
    // Stable ordering for consistent signature
    rows.sort((a, b) => {
      const ai = Number(a.id ?? 0);
      const bi = Number(b.id ?? 0);
      if (ai !== bi) return ai - bi;
      return String(a.employeeId ?? "").localeCompare(String(b.employeeId ?? ""));
    });
    return JSON.stringify(rows);
  }, [activeEnrollments]);

  React.useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    const run = async () => {
      // Only compute missing premiums (when totalPremium is null/blank)
      const baseRows = activeEnrollments.filter((e) => e.dependentId == null);
      const missingRows = baseRows.filter((e) => {
        const raw = (e as any).totalPremium;
        if (raw == null) return true;
        const s = String(raw).trim();
        if (s === "") return true;
        const n = Number.parseFloat(s);
        return !Number.isFinite(n);
      });

      if (missingRows.length === 0) {
        if (mounted) setComputedTotalsByPlanId(new Map());
        return;
      }

      const { signal } = controller;

      // Precompute which dependentIds are enrolled per employee+plan from ACTIVE rows only
      const enrolledDepIdsByEmpPlan = new Map<string, Set<number>>();
      for (const r of activeEnrollments as any[]) {
        if (!Number.isFinite(r?.dependentId)) continue;
        const key = `${r.employeeId}:${r.planId}`;
        const set = enrolledDepIdsByEmpPlan.get(key) || new Set<number>();
        set.add(r.dependentId);
        enrolledDepIdsByEmpPlan.set(key, set);
      }

      // DEV-262: shared loader replaces the hand-rolled contributions fetch +
      // per-plan rate fetches. It also brings referenced buy-up BASE plans'
      // rows, which the old code never fetched. Errors fall back to empty
      // maps, matching the old forgiving behavior for this overview total.
      const planIds = Array.from(new Set(missingRows.map((e) => e.planId))).filter(
        (id) => Number.isFinite(id as any)
      ) as number[];
      let premiumData: Awaited<ReturnType<typeof fetchPlanPremiumData>>;
      try {
        premiumData = await fetchPlanPremiumData(planIds, clientId);
      } catch {
        premiumData = {
          allPlanContributions: [],
          allPlanRates: [],
          ratesByPlanId: {},
          contribsByPlanId: {},
          isLoading: false,
        };
      }

      // Fetch dependents once per unique employeeId
      const employeeIds = Array.from(
        new Set(missingRows.map((e) => e.employeeId))
      ).filter((id) => Number.isFinite(id as any)) as number[];
      const dependentsByEmployeeId = new Map<number, any[]>();
      await Promise.all(
        employeeIds.map(async (employeeId) => {
          try {
            const depsRes = await fetch(`/api/employees/${employeeId}/dependents`, {
              signal,
              cache: "no-store",
            });
            const deps = depsRes.ok ? await depsRes.json() : [];
            dependentsByEmployeeId.set(employeeId, Array.isArray(deps) ? deps : []);
          } catch {
            dependentsByEmployeeId.set(employeeId, []);
          }
        })
      );

      const totals = new Map<number, number>();
      for (const e of missingRows) {
        const plan = plansData.find((p) => p.id === e.planId);
        const emp = employees.find((x) => x.id === e.employeeId);
        if (!plan || !emp) continue;

        try {
          const planRates = premiumData.ratesByPlanId[plan.id] || [];
          const planContribs = premiumData.contribsByPlanId[plan.id] || [];

          const allEmployeeDependents =
            dependentsByEmployeeId.get(emp.id) || ([] as any[]);
          const depKey = `${e.employeeId}:${e.planId}`;
          const depIds = enrolledDepIdsByEmpPlan.get(depKey) || new Set<number>();
          const coveredDependents = (allEmployeeDependents as any[]).filter((d) =>
            depIds.has(d.id)
          );

          const result = calculateEnrollmentPremium({
            plan: getEffectivePlan(plan as any) as any,
            planRates,
            planContributions: planContribs as any,
            employee: emp,
            dependents: coveredDependents,
            coverageTier: (e as any).coverageTier,
            // DEV-262: base-plan rows so a buy-up plan's employer share
            // resolves (was omitted before; harmless for the gross total,
            // correct if the split is ever read from this result)
            allPlanContributions: premiumData.allPlanContributions as any,
            allPlanRates: premiumData.allPlanRates as any,
          }) as any;

          const add = Number(result?.totalMonthlyRate || 0);
          if (!Number.isFinite(add)) continue;
          const prev = totals.get(plan.id) || 0;
          totals.set(plan.id, Math.round((prev + add) * 100) / 100);
        } catch {
          // ignore this enrollment on failure
        }
      }

      if (mounted) setComputedTotalsByPlanId(totals);
    };
    run();
    return () => {
      mounted = false;
      controller.abort();
    };
  }, [
    clientId,
    premiumComputeSignature,
    plansData,
    employees,
  ]);

  const formatCurrency0 = (n: number) =>
    n.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // Fetch the client's per-plan eligibility settings so the New Hires +
  // Recently Eligible tables use the actual plan rules (waitingPeriod /
  // waitingPeriodType / entryDate) instead of the legacy hardcoded "30 days +
  // first of following month" defaults. Same query key the Eligibility tab
  // uses, so React Query dedupes — no extra network round-trip.
  const { data: eligibilitySettings = [] } = useQuery<ClientPlanEligibilitySetting[]>({
    queryKey: ["/api/client-plan-eligibility", clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const res = await fetch(`/api/client-plan-eligibility?clientId=${clientId}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!clientId,
  });

  // Most permissive plan wins; "immediate / 0 days" when nothing is configured.
  // The rule is shown next to the New Hires table so the broker can tell the
  // two apart.
  const enrollmentRule = resolveEnrollmentRule(eligibilitySettings);
  const enrollmentConfig = enrollmentRule.config;
  const enrollmentRulePlanName =
    enrollmentRule.planId != null ? plansData.find((p) => p.id === enrollmentRule.planId)?.name : undefined;
  const enrollmentRuleLabel = describeEnrollmentRule(enrollmentRule, enrollmentRulePlanName);

  // Calculate new hires in initial enrollment period using shared utility
  const newHiresInEnrollmentPeriod = filterNewHires(clientEmployees, enrollmentConfig);

  // Calculate recently eligible employees using shared utility
  const recentlyEligibleEmployees = filterRecentlyEligible(clientEmployees, enrollmentConfig);

  // Drop hire-date fallback rows for employees that already have an
  // eligibility_event row — the API row is authoritative and has accurate
  // dates, so showing both produces a visible duplicate.
  const eventEmployeeIds = new Set(
    eligibilityEvents.map((e: any) => e.employeeId),
  );
  const recentlyEligibleNotInEvents = recentlyEligibleEmployees.filter(
    (emp) => !eventEmployeeIds.has(emp.id),
  );

  // Check how many enrollments each new hire has
  const getEmployeeEnrollmentCount = (employeeId: number) => {
    return activeEnrollments.filter(
      (e) => e.employeeId === employeeId && e.dependentId == null
    ).length;
  };

  // Get enrollment submission status based on enrollmentProgress field
  // Complete = submitted, confirmed, or under_review (signed off and submitted)
  // In Progress = viewed or in_progress (started but not submitted)
  // Not Started = no enrollments or all are not_started
  //
  // Source from ALL client enrollments (not just currently-effective ones): a
  // new hire who just submitted has a `pending`/future-effective enrollment that
  // `activeEnrollments` excludes — using that list showed "Not Started" even
  // after submission. Submission status depends on enrollmentProgress, not on
  // whether coverage is active yet. Drop cancelled/deleted rows.
  const getEmployeeEnrollmentStatus = (employeeId: number): "complete" | "in_progress" | "not_started" => {
    const employeeEnrollments = (clientEnrollments as any[]).filter(
      (e) =>
        e.employeeId === employeeId &&
        e.dependentId == null &&
        !["cancelled", "canceled", "deleted"].includes(
          String(e.status || "").toLowerCase()
        )
    );

    if (employeeEnrollments.length === 0) {
      return "not_started";
    }
    
    // Check if any enrollment is submitted/confirmed (complete)
    const hasSubmitted = employeeEnrollments.some((e) => {
      const progress = (e as any).enrollmentProgress?.toLowerCase() || "not_started";
      return ["submitted", "confirmed", "under_review"].includes(progress);
    });
    
    if (hasSubmitted) {
      return "complete";
    }
    
    // Check if any enrollment is in progress
    const hasInProgress = employeeEnrollments.some((e) => {
      const progress = (e as any).enrollmentProgress?.toLowerCase() || "not_started";
      return ["in_progress", "viewed"].includes(progress);
    });
    
    if (hasInProgress) {
      return "in_progress";
    }

    // Employee has active enrollments but enrollmentProgress was never updated
    // (legacy data or broker-created before the fix) — at least "in_progress"
    return "in_progress";
  };

  return (
    <div className="space-y-6">
      {/* Overview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      Carrier
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      Coverage Type
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      Plans
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      Enrolled
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      Total Monthly Premium
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    if (!availablePlans?.length) {
                      return (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-8 text-center text-muted-foreground"
                            data-testid={upcomingMessage ? "overview-upcoming-plans" : undefined}
                          >
                            {upcomingMessage ?? "No plans configured"}
                          </td>
                        </tr>
                      );
                    }

                    const grouped = new Map<
                      string,
                      {
                        carrierId: number | null | undefined;
                        carrierName: string | null | undefined;
                        type: string | undefined;
                        planCount: number;
                        planIds: Set<number>;
                        totalPremium: number;
                      }
                    >();
                    for (const plan of availablePlans) {
                      const effectivePlan = getEffectivePlan(plan);
                      const key = `${effectivePlan.carrierId ?? "null"}::${(
                        effectivePlan.type || ""
                      ).toLowerCase()}`;
                      const base = grouped.get(key) || {
                        carrierId: effectivePlan.carrierId,
                        carrierName: (effectivePlan as any).carrier,
                        type: effectivePlan.type,
                        planCount: 0,
                        planIds: new Set<number>(),
                        totalPremium: 0,
                      };
                      base.planCount += 1;
                      base.planIds.add(plan.id);
                      const agg = planAggregates.get(plan.id);
                      // Prefer computed totals when available; fallback to persisted totals
                      const computed = computedTotalsByPlanId.get(plan.id);
                      const persisted = agg?.totalPremium || 0;
                      const computedMissing = Number.isFinite(computed as any)
                        ? (computed as number)
                        : 0;
                      base.totalPremium += persisted + computedMissing;
                      grouped.set(key, base);
                    }

                    const sortedRows = Array.from(grouped.values()).sort(
                      (a, b) =>
                        planTypeRank(a.type) - planTypeRank(b.type) ||
                        (a.carrierName || "").localeCompare(b.carrierName || ""),
                    );
                    return sortedRows.map((row, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/25">
                        <td className="px-4 py-3 text-sm">
                          {row.carrierName || "Unknown Carrier"}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline" className="capitalize">
                            {row.type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">
                          {row.planCount}
                        </td>
                        <td className="px-4 py-3 text-sm" data-testid="overview-enrolled-count">
                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {/* Same rule as Available Plans "Total Enrolled"
                                (DEV-317): distinct employees across the
                                group's plans, pending elections included. */}
                            {formatEnrolledLabel(
                              planEnrollmentCounts(
                                (clientEnrollments as any[]).filter((e) => row.planIds.has(e.planId)),
                              ),
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {formatCurrency0(row.totalPremium)}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enrollment Status Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Enrollment Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">New Hires - Enrollment Period</h4>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <UserPlus className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {newHiresInEnrollmentPeriod.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Newly Eligible</h4>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {
                        clientEmployees.filter((emp) => {
                          if (!emp.hireDate) return false;
                          const hireDate = parseDateLocal(emp.hireDate);
                          if (!hireDate) return false;
                          const eligibleDate = new Date(hireDate);
                          eligibleDate.setDate(eligibleDate.getDate() + 60); // 60-day waiting period
                          const now = new Date();
                          return (
                            eligibleDate <= now &&
                            eligibleDate >=
                              new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
                          );
                        }).length
                      }
                    </p>
                    <p className="text-xs text-muted-foreground">This month</p>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Pending Changes</h4>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                    <Clock className="h-4 w-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">0</p>
                    <p className="text-xs text-muted-foreground">
                      Awaiting approval
                    </p>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-2">Qualifying Events</h4>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">0</p>
                    <p className="text-xs text-muted-foreground">
                      Active windows
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* New Hires in Initial Enrollment Period */}
            {newHiresInEnrollmentPeriod.length > 0 && (
              <div className="border rounded-lg">
                <div className="px-4 py-3 border-b bg-muted/50">
                  <h4 className="font-semibold text-base flex items-center gap-2">
                    <UserPlus className="h-5 w-5" />
                    New Hires - Initial Enrollment Period
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="new-hires-rule">
                    {enrollmentRuleLabel}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <Table data-testid="new-hires-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead className="text-center">Hire Date</TableHead>
                        <TableHead className="text-center">Employment</TableHead>
                        <TableHead className="text-center">Hours/Wk</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Eligibility Date</TableHead>
                        <TableHead className="text-center">Effective Date</TableHead>
                        <TableHead className="text-center">Enrollment Due</TableHead>
                        <TableHead className="text-center">Days Until</TableHead>
                        <TableHead className="text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {newHiresInEnrollmentPeriod.map((emp) => {
                        const { enrollmentInfo } = emp;
                        const enrollmentStatus = getEmployeeEnrollmentStatus(emp.id);

                        return (
                          <TableRow key={emp.id}>
                            <TableCell>
                              <Link href={`/clients/${clientId}/employees/${emp.id}`}>
                                <span className="font-medium text-primary hover:underline cursor-pointer">
                                  {emp.firstName} {emp.lastName}
                                </span>
                              </Link>
                            </TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(emp.hireDate)}</TableCell>
                            <TableCell className="text-sm text-center">{(emp as any).employmentType || "Not provided"}</TableCell>
                            <TableCell className="text-sm text-center">{(emp as any).scheduledHours || "—"}</TableCell>
                            <TableCell className="text-center">
                              {enrollmentStatus === "complete" ? (
                                <Badge className="bg-green-100 text-green-700 text-[10px]">Complete</Badge>
                              ) : enrollmentStatus === "in_progress" ? (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px]">In Progress</Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-600 border-gray-300 text-[10px]">Not Started</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(enrollmentInfo.eligibilityDate)}</TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(enrollmentInfo.effectiveDate)}</TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(enrollmentInfo.enrollmentDueDate)}</TableCell>
                            <TableCell className="text-center">
                              {/* Days until the Due Date in the previous column — not
                                  days left in the 30-day window, which read as a
                                  countdown to a date that had already passed. */}
                              <span className={`text-sm font-medium ${enrollmentInfo.daysUntilDue <= 5 ? "text-orange-600" : ""}`}>
                                {formatDaysUntilDue(enrollmentInfo.daysUntilDue)}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              {enrollmentStatus === "complete" ? (
                                <Link href={`/clients/${clientId}/employees/${emp.id}?tab=benefits`}>
                                  <Button variant="outline" size="sm" className="h-7 text-xs">
                                    <Eye className="h-3 w-3 mr-1" />
                                    View
                                  </Button>
                                </Link>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    openReminderModal(emp.id, `${emp.firstName} ${emp.lastName}`, enrollmentInfo.enrollmentDueDate);
                                  }}
                                >
                                  <Bell className="h-3 w-3 mr-1" />
                                  Remind
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="border rounded-lg">
              <div className="px-4 py-3 border-b bg-muted/50">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Recent Eligibility Changes
                </h4>
              </div>
              {eligibilityEventsLoading ? (
                <div className="p-4 text-center py-6 text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50 animate-pulse" />
                  <p className="text-sm">Loading eligibility changes...</p>
                </div>
              ) : eligibilityEvents.length === 0 && recentlyEligibleNotInEvents.length === 0 ? (
                <div className="p-4 text-center py-6 text-muted-foreground">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent eligibility changes</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table data-testid="recent-eligibility-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead className="text-center">Change Date</TableHead>
                        <TableHead className="text-center">Employment</TableHead>
                        <TableHead className="text-center">Hours/Wk</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Eligibility Date</TableHead>
                        <TableHead className="text-center">Effective Date</TableHead>
                        <TableHead className="text-center">Enrollment Due</TableHead>
                        <TableHead className="text-center">Days Until</TableHead>
                        <TableHead className="text-center">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eligibilityEvents.map((event) => {
                        const employee = clientEmployees.find(e => e.id === event.employeeId);
                        const enrollmentStatus = employee ? getEmployeeEnrollmentStatus(employee.id) : "not_started";

                        return (
                          <TableRow key={`event-${event.id}`}>
                            <TableCell>
                              <Link href={`/clients/${clientId}/employees/${event.employeeId}`}>
                                <span className="font-medium text-primary hover:underline cursor-pointer">
                                  {event.employeeName || (employee ? `${employee.firstName} ${employee.lastName}` : "Unknown")}
                                </span>
                              </Link>
                            </TableCell>
                            <TableCell className="text-sm text-center">
                              {new Date(event.createdAt).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </TableCell>
                            <TableCell className="text-sm text-center">
                              {event.employmentType || (employee as any)?.employmentType || "Not provided"}
                            </TableCell>
                            <TableCell className="text-sm text-center">{(employee as any)?.scheduledHours || "—"}</TableCell>
                            <TableCell className="text-center">
                              {event.isEligible ? (
                                <Badge className="bg-green-100 text-green-700 text-[10px]">Gaining Eligibility</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 text-[10px]">Losing Eligibility</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(event.eligibilityDate)}</TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(event.effectiveDate)}</TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(event.enrollmentDueDate)}</TableCell>
                            <TableCell className="text-center">
                              {event.enrollmentDueDate ? (() => {
                                const dueDate = parseDateLocal(event.enrollmentDueDate) ?? new Date(event.enrollmentDueDate + 'T00:00:00');
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const diffDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                                return (
                                  <span className={`text-sm font-medium ${diffDays <= 5 ? "text-orange-600" : ""}`}>
                                    {formatDaysUntilDue(diffDays)}
                                  </span>
                                );
                              })() : (
                                <span className="text-sm font-medium text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {enrollmentStatus === "complete" ? (
                                <Link href={`/clients/${clientId}/employees/${event.employeeId}?tab=benefits`}>
                                  <Button variant="outline" size="sm" className="h-7 text-xs">
                                    <Eye className="h-3 w-3 mr-1" />
                                    View
                                  </Button>
                                </Link>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    const empName = event.employeeName || (employee ? `${employee.firstName} ${employee.lastName}` : "Employee");
                                    openReminderModal(event.employeeId, empName, event.enrollmentDueDate);
                                  }}
                                >
                                  <Bell className="h-3 w-3 mr-1" />
                                  Remind
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {recentlyEligibleNotInEvents.map((emp) => {
                        const { eligibilityInfo } = emp;
                        const enrollmentStatus = getEmployeeEnrollmentStatus(emp.id);

                        return (
                          <TableRow key={`hire-${emp.id}`}>
                            <TableCell>
                              <Link href={`/clients/${clientId}/employees/${emp.id}`}>
                                <span className="font-medium text-primary hover:underline cursor-pointer">
                                  {emp.firstName} {emp.lastName}
                                </span>
                              </Link>
                            </TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(emp.hireDate)}</TableCell>
                            <TableCell className="text-sm text-center">{(emp as any).employmentType || "Not provided"}</TableCell>
                            <TableCell className="text-sm text-center">{(emp as any).scheduledHours || "—"}</TableCell>
                            <TableCell className="text-center">
                              {enrollmentStatus === "complete" ? (
                                <Badge className="bg-green-100 text-green-700 text-[10px]">Complete</Badge>
                              ) : enrollmentStatus === "in_progress" ? (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px]">In Progress</Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-600 border-gray-300 text-[10px]">Not Started</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(eligibilityInfo.eligibilityDate)}</TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(eligibilityInfo.effectiveDate)}</TableCell>
                            <TableCell className="text-sm text-center">{formatDateSafe(eligibilityInfo.enrollmentDueDate)}</TableCell>
                            <TableCell className="text-center">
                              <span className={`text-sm font-medium ${eligibilityInfo.daysUntilDue <= 5 ? "text-orange-600" : ""}`}>
                                {formatDaysUntilDue(eligibilityInfo.daysUntilDue)}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">
                              {enrollmentStatus === "complete" ? (
                                <Link href={`/clients/${clientId}/employees/${emp.id}?tab=benefits`}>
                                  <Button variant="outline" size="sm" className="h-7 text-xs">
                                    <Eye className="h-3 w-3 mr-1" />
                                    View
                                  </Button>
                                </Link>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    openReminderModal(emp.id, `${emp.firstName} ${emp.lastName}`, eligibilityInfo.enrollmentDueDate);
                                  }}
                                >
                                  <Bell className="h-3 w-3 mr-1" />
                                  Remind
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enrollment Reminder Modal */}
      <Dialog open={reminderModalOpen} onOpenChange={setReminderModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Send Enrollment Reminder
            </DialogTitle>
            <DialogDescription>
              Send a reminder to {reminderEmployee?.name} to complete their benefits enrollment
              {reminderEmployee?.enrollmentDue && (
                <span className="block mt-1 text-orange-600 font-medium">
                  Enrollment due: {formatDateSafe(reminderEmployee.enrollmentDue)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reminder-message">Message</Label>
              <Textarea
                id="reminder-message"
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                rows={8}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReminderModalOpen(false)}
              disabled={sendingReminder}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendReminder}
              disabled={sendingReminder || !reminderMessage.trim()}
            >
              {sendingReminder ? (
                <>Sending...</>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Reminder
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BenefitsOverview;
